import React, { useMemo, useState, useCallback } from "react";
import { z } from "zod";
import { Button } from "storybook/components/button";
import toast from "storybook/components/toast";
import { SPECIALIST_HOSTNAME, SUPPORTED_LANGUAGES } from "env-vars";
import { Typography } from "storybook/components/typography";
import { ChevronRight } from "@material-ui/icons";
import { CircularProgress } from "@material-ui/core";
import { useForm } from "storybook/utils/form";
import { usePreferredLanguage, Language } from "context/preferred-language";
import { useTransifexLoader } from "context/transifex-loader";
import {
  MainContainer,
  LinkWrapper,
  SettingsLink,
  RadioWrapper,
  LanguageDescriptionWrapper,
  LoadingWrapper,
} from "./styles";

const schema = z.object({
  language: z
    .enum([Language.ENGLISH, Language.SPANISH, Language.PORTUGUESE])
    .nullable(),
});

type SchemaType = z.infer<typeof schema>;

const languageMap = {
  [Language.ENGLISH]: "English",
  [Language.SPANISH]: "Español",
  [Language.PORTUGUESE]: "Português",
};

export function Settings() {
  const supportedLanguages: Language[] = SUPPORTED_LANGUAGES.split(",");
  const [isSaving, setIsSaving] = useState(false);
  const { setLang } = useTransifexLoader();

  const { preferredLanguage, setPreferredLanguage, isLoading } =
    usePreferredLanguage();

  const values = useMemo(
    () => ({
      language: preferredLanguage,
    }),
    [preferredLanguage]
  );

  const { Form, Radio, getFieldState, reset } = useForm<SchemaType>({
    schema,
    values,
  });

  const isDirty = getFieldState("language").isDirty;

  const handleSubmit = useCallback(
    async (data: SchemaType) => {
      setIsSaving(true);
      const patientId = localStorage.getItem("ciq_id");
      const idToken = localStorage.getItem("id_token");

      try {
        const response = await fetch(
          `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              preferred_language: data.language,
            }),
          }
        );

        if (response.ok) {
          setPreferredLanguage(data.language as Language);
          toast.success("Preferred language updated successfully");
        }

        // Switch the Live project language
        setLang(data.language as Language);

        // Reset the form's dirty state
        reset();
      } catch (error) {
        console.error("Error saving preferred language:", error);
        toast.error("Error saving preferred language");
      } finally {
        setIsSaving(false);
      }
    },
    [reset, setPreferredLanguage, setLang]
  );

  return (
    <MainContainer data-testid="Settings">
      <div>
        <Typography
          theme="default"
          variant="body1"
          component="h2"
          color="ciqBrandDark"
        >
          <b>Personal Information</b>
        </Typography>
        <LinkWrapper>
          <SettingsLink to="settings/personal-information">
            <span>Update Personal Information</span>
            <ChevronRight />
          </SettingsLink>
        </LinkWrapper>
        <LinkWrapper>
          <SettingsLink to="settings/contact-information">
            <span>Contact Information</span>
            <ChevronRight />
          </SettingsLink>
        </LinkWrapper>
      </div>
      <div>
        <Typography
          theme="default"
          variant="body1"
          component="h2"
          color="ciqBrandDark"
        >
          <b>Privacy</b>
        </Typography>
        <LinkWrapper>
          <SettingsLink to="settings/consent">
            <span>Data Consent</span>
            <ChevronRight />
          </SettingsLink>
        </LinkWrapper>
      </div>
      <div>
        <Typography
          theme="default"
          variant="body1"
          component="h2"
          color="ciqBrandDark"
        >
          <b>Preferred Language</b>
        </Typography>
        <LanguageDescriptionWrapper>
          <Typography theme="default" variant="subtitle2" color="ciqBrandDark">
            Select your preferred language to ensure all communications and
            content are displayed in your chosen language.
          </Typography>
        </LanguageDescriptionWrapper>
        {isLoading ? (
          <LoadingWrapper>
            <CircularProgress />
          </LoadingWrapper>
        ) : (
          <Form onSubmit={handleSubmit}>
            <RadioWrapper>
              <div className="notranslate">
                {supportedLanguages.map((language) => (
                  <Radio
                    key={language}
                    theme="default"
                    name="language"
                    value={language}
                    displayValue={<span>{languageMap[language]}</span>}
                  />
                ))}
              </div>
              <Button
                theme="default"
                variant="outlined"
                type="submit"
                disabled={!isDirty || isSaving}
              >
                Save
              </Button>
            </RadioWrapper>
          </Form>
        )}
      </div>
    </MainContainer>
  );
}
